'use client'
import React from 'react'
import Link from 'next/link'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export type AnnouncementModalContent = {
    type?: string
  badge: string
  title: string
  body: string
  imageUrl?: string | null
  ctaLabel: string
  secondCtaLabel?: string
  ctaUrl?: string | null
  learnMoreUrl?: string | null
  learnMoreLabel?: string
  onCtaClick?: () => void | Promise<void>
  secondOnCtaClick? : () => void | Promise<void>
}

const isExternalUrl = (url: string) =>
  /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url) || /^(mailto:|tel:)/i.test(url)

interface AnnouncementModalProps {
  show: boolean
  onClose: () => void
  content: AnnouncementModalContent
}

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  show,
  onClose,
  content,
}) => {
  React.useEffect(() => {
    if (!show) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [show, onClose])

  React.useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [show])

  if (!show) return null

  return (
    <>
      <style>{`
        @keyframes am-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes am-card-in {
          from {
            opacity: 0;
            transform: translateY(28px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .am-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(10, 10, 20, 0.65);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 1055;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: am-backdrop-in 200ms ease forwards;
        }

        .am-card {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border-radius: 22px;
          box-shadow:
            0 24px 60px rgba(15, 23, 42, 0.14),
            0 6px 18px rgba(15, 23, 42, 0.08);
          max-width: 720px;
          width: 100%;
          overflow: hidden;
          animation: am-card-in 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          position: relative;
        }

        .am-card-header {
          padding: 24px 28px 6px;
          position: relative;
          overflow: hidden;
        }

        .am-card-header::before {
          content: '';
          position: absolute;
          inset: 0;
          background:${
            content.type === 'warning' ?
            `radial-gradient(circle at top left, rgba(255, 215, 0, 0.15) 0%, rgba(255, 255, 255, 0) 50%),
            radial-gradient(circle at bottom right, rgba(245, 158, 11, 0.08), transparent 40%);
            ` 
            : ` radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 38%),
            radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.08), transparent 35%);`
          } 
            
          pointer-events: none;
        }

        .am-icon-wrap {
          width: 42px;
          height: 42px;
          border-radius: 999px;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          position: relative;
          z-index: 1;
        }

        .am-badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          color: ${ content.type === 'warning' ? 'rgb(180 83 9)' : '#2563eb'} ;
          background: ${ content.type === 'warning' ? 'rgb(254 243 199)' : 'rgba(37, 99, 235, 0.08)'};
          border: ${ content.type === 'warning' ? '1px solid rgb(253 230 138)' : '1px solid rgba(37, 99, 235, 0.14)'} ;
          border-radius: 99px;
          padding: 7px 12px;
          margin-bottom: 14px;
          position: relative;
          z-index: 1;
        }

        .am-title {
          font-size: clamp(1.55rem, 2.9vw, 2.8rem);
          font-weight: 800;
          color: #173b7a;
          line-height: 1.08;
          letter-spacing: -0.03em;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        .am-title strong {
          color: #d61f1f;
        }

        .am-title p {
          margin: 0;
        }

        .am-title > :first-child {
          margin-top: 0;
        }

        .am-title > :last-child {
          margin-bottom: 0;
        }

        .am-body {
          padding: 14px 28px 20px;
          flex: 1;
          position: relative;
          z-index: 1;
        }

        .am-feature-image {
          display: block;
          width: calc(100% - 56px);
          margin: 0 28px 12px;
          max-height: 240px;
          object-fit: cover;
          border-radius: 18px;
          position: relative;
          z-index: 1;
        }

        .am-markdown {
          max-width: 560px;
          color: #374151;
          font-size: 1rem;
          line-height: 1.62;
        }

        .am-markdown p {
          margin: 0 0 10px;
        }

        .am-markdown ul {
          margin: 16px 0 0;
          padding: 0;
          list-style: none;
          display: grid;
          gap: 12px;
        }

        .am-markdown li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          color: #374151;
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.42;
        }

        .am-markdown li::before {
          content: '✓';
          flex: 0 0 auto;
          width: 22px;
          height: 22px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(34, 197, 94, 0.14);
          color: #16a34a;
          font-size: 15px;
          font-weight: 900;
          line-height: 1;
          position: relative;
          top: 2px;
        }

        .am-markdown strong {
          color: #111827;
          font-weight: 700;
        }

        .am-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e5e7eb 12%, #e5e7eb 88%, transparent);
          margin: 0 28px;
        }

        .am-footer {
          padding: 18px 28px 28px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: auto;
        }

        .am-btn-primary {
          flex: 1;
          min-width: 180px;
          background: ${ content.type === 'warning' ? 'rgb(251 191 36)' : 'linear-gradient(135deg, #ff4a5f 0%, #ff2f57 100%)'} ;
          color: ${ content.type === 'warning' ? '#173b7a' : '#fff'} ;
          border: none;
          border-radius: 12px;
          padding: 15px 20px;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;
          text-align: center;
          box-shadow: 0 14px 26px rgba(255, 47, 87, 0.24);
        }

        .am-btn-primary:hover {
          transform: translateY(-1px);
          filter: brightness(1.02);
          box-shadow: 0 18px 30px rgba(255, 47, 87, 0.28);
        }

        .am-btn-primary:active {
          transform: translateY(0);
        }

        .am-btn-link {
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 150ms;
        }

        .am-btn-link:hover {
          color: #374151;
        }

        .am-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(148, 163, 184, 0.22);
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          transition: background 150ms, color 150ms;
          line-height: 1;
          font-size: 18px;
        }

        .am-close-btn:hover {
          background: #ffffff;
          color: #0f172a;
        }

        .am-markdown a {
          color: #2563eb;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .am-backdrop { animation: none; }
          .am-card     { animation: none; }
        }

        @media (max-width: 640px) {
          .am-card-header,
          .am-body,
          .am-footer {
            padding-left: 18px;
            padding-right: 18px;
          }

          .am-title {
            font-size: 1.65rem;
            line-height: 1.1;
          }

          .am-markdown li {
            font-size: 0.96rem;
          }

          .am-btn-primary {
            min-width: 100%;
            font-size: 14px;
          }

          .am-divider {
            margin: 0 18px;
          }

          .am-feature-image {
            width: calc(100% - 36px);
            margin-left: 18px;
            margin-right: 18px;
            max-height: 180px;
          }
        }
      `}</style>

      <div
        className="am-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="am-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div className="am-card">

          <div className="am-card-header">
            <button
              className="am-close-btn"
              onClick={onClose}
              aria-label="Fechar anúncio"
            >
              ✕
            </button>

            <div className="am-badge">{content.badge}</div>

            <div className="am-title" id="am-title">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content.title}
              </ReactMarkdown>
            </div>
          </div>

          {content.imageUrl ? (
            <img
              className="am-feature-image"
              src={content.imageUrl}
              alt={content.title}
              loading="lazy"
            />
          ) : null}

          <div className="am-body">
            <div className="am-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content.body}
              </ReactMarkdown>
            </div>
          </div>

          <div className="am-divider" />

        {content.ctaUrl && (
          <div className="am-footer">
            {content.onCtaClick ? (
              <div className="d-flex gap-2 flex-wrap w-100">
              <button
                className="am-btn-primary"
                onClick={() => {
                  void content.onCtaClick?.()
                }}
              >
                {content.ctaLabel}
              </button>
              {content.secondCtaLabel && (
                <button className="am-btn-primary" onClick={() => {
                  void content.secondOnCtaClick?.()
                }}>
                  {content.secondCtaLabel}
                </button>
              )}
              </div>
            ) : content.ctaUrl ? (
              isExternalUrl(content.ctaUrl) ? (
                <a
                  href={content.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="am-btn-primary"
                >
                  {content.ctaLabel}
                </a>
              ) : (
                <Link href={content.ctaUrl} className="am-btn-primary">
                  {content.ctaLabel}
                </Link>
              )
            ) : (
              <button className="am-btn-primary" onClick={onClose}>
                {content.ctaLabel}
              </button>
            )}

            {content.learnMoreUrl && (
              isExternalUrl(content.learnMoreUrl) ? (
                <a
                  href={content.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="am-btn-link"
                >
                  {content.learnMoreLabel ?? ''}
                </a>
              ) : (
                <Link href={content.learnMoreUrl} className="am-btn-link">
                  {content.learnMoreLabel ?? ''}
                </Link>
              )
            )}
          </div>
          
        )}

        </div>
      </div>
    </>
  )
}
